import {
  EmberSignal, EventProgress, EventProgressJson, EventsData, GearItem, HubMarks, LevelModifiers, LiveEventData, PaidReward, TrialState, TrialStateJson, WeeklyTrial,
  battleSignals, eventAt, eventNodeSeed, eventsUnlocked, isStaleEvent, heroicMarks, heroicModifiers, heroicOpen, metaSeed, nextEvent, payReward, sameModifiers, trialModifiers,
} from "@highmarch/domain";
import { HasSections, registerSaveSection } from "./saveSections";
import type { LevelCatalog } from "./levelCatalog";

/**
 * Events and the endgame's replays (FINISH_PLAN WP6, MP-21, DESIGN §10.5) as use cases: the dated events and the weekly
 * rotation with their free tracks, embers from each event's own sources (a battle's end, Daily Orders, upgrades, Tribute,
 * the Daily Road), the Weekly Trial and the Heroic tier. The state lives in two save
 * sections — `events` (embers, claims, emblems) and `trials` (the week's locked Trial, Heroic stars) — beside the
 * catalog, which saves them with the rest of the progress (LevelCatalog.applyTo).
 */

/** A section's raw JSON when it is an object; the domain `fromJson` validates the fields. */
function asObject<T>(v: unknown): T | undefined {
  return typeof v === "object" && v !== null && !Array.isArray(v) ? (v as T) : undefined;
}

export const eventsSection = registerSaveSection<EventProgressJson>("events", asObject, (v) => v);
export const trialsSection = registerSaveSection<TrialStateJson>("trials", asObject, (v) => v);

/** Seed tag of the Weekly Trial's chest (apart from the event nodes' and every other meta roll). */
const TRIAL_CHEST_TAG = "trialChest";

export class EndgameState {
  constructor(readonly events: EventProgress, readonly trials: TrialState) {}
  static fresh(): EndgameState { return new EndgameState(new EventProgress(), new TrialState()); }
  static fromSave(save: HasSections): EndgameState {
    return new EndgameState(EventProgress.fromJson(eventsSection.read(save)), TrialState.fromJson(trialsSection.read(save)));
  }
  applyTo(save: HasSections): void {
    eventsSection.write(save, this.events.toJson());
    trialsSection.write(save, this.trials.toJson());
  }
}

/** Which endgame a run was: its level and modifiers against the Heroic rules and the locked Trial. */
export type RunKind = "heroic" | "trial" | null;

/**
 * A finished run as the endgame sees it. `key` identifies the run (its seed), so it counts once. The rest feed the live
 * event's sources (MP-21); absent = none (older callers and tests).
 */
export interface FinishedRun {
  readonly level: number;
  readonly won: boolean;
  readonly stagesCleared: number;
  readonly modifiers: LevelModifiers;
  readonly key: string;
  readonly now: number;
  /** Stars the run earned. */
  readonly stars?: number;
  readonly leaks?: number;
  /** The battle's tally (domain BattleTally keys: kills per enemy, elites, wavesCalled). */
  readonly counts?: Readonly<Record<string, number>>;
  readonly rareKills?: number;
}

/** What a finished run earned: embers for the live event, a Heroic star and its Marks, the Trial's reward. */
export interface RunOutcome {
  readonly kind: RunKind;
  readonly event: LiveEventData | null;
  readonly embers: number;
  /** A first Heroic win of the level: its star lit and `heroicMarks` paid. */
  readonly heroicFirst: boolean;
  readonly heroicMarks: number;
  /** MP-23: Trophies the first Heroic win paid (events.json heroic.trophies; 0 otherwise). */
  readonly heroicTrophies?: number;
  /** The week's Trial won for the first time: its reward paid (with the chest banked). */
  readonly trialReward: PaidReward | null;
  readonly gear: readonly GearItem[];
  /** Marks for the Event shop (docs/SHOP.md §9.3): the Trial's first win of the week, a level's first Heroic win. */
  readonly eventMarks: number;
}

/** A claimed node: what it paid, the gear its chest held and its Event-shop Marks (a rotation week's nodes only). */
export interface NodeClaim { readonly node: number; readonly paid: PaidReward; readonly gear: readonly GearItem[]; readonly eventMarks: number }

export class Endgame {
  constructor(private readonly catalog: LevelCatalog) {}

  private get data(): EventsData { return this.catalog.balance.events; }
  private get state(): EndgameState { return this.catalog.endgame; }
  get progress(): EventProgress { return this.state.events; }
  get trials(): TrialState { return this.state.trials; }

  /** Events count and pay once the player has cleared the unlock level. */
  get open(): boolean { return eventsUnlocked(this.data, this.catalog.highestLevelCleared); }
  get unlockAfterLevel(): number { return this.data.unlockAfterLevel; }
  live(now: number): LiveEventData | null { return eventAt(this.data, now); }
  upcoming(now: number): LiveEventData | null { return nextEvent(this.data, now); }

  /**
   * At load: forgets recurring event weeks long over (meta/events.ts isStaleEvent) — their embers and claims here, and
   * their ids in `seen` (the save's seenEvents) — so a weekly event does not grow the save by an id a week. Returns
   * `seen` without them.
   */
  pruneEnded(now: number, seen: readonly string[]): string[] {
    const stale = (id: string) => isStaleEvent(this.data, id, now);
    this.progress.prune(stale);
    return seen.filter((id) => !stale(id));
  }

  /** Counts Daily Orders claimed while the live event runs (each once). Returns the embers added. */
  syncOrders(now: number): number {
    const e = this.live(now);
    if (!e || !this.open) return 0;
    const claimed = this.catalog.orders.orders.filter((o) => o.claimed).map((o) => o.uid);
    return this.progress.creditOrders(e, claimed);
  }

  /** The hub's totals the events compare (upgrades completed, Tribute collected). */
  private hubMarks(): HubMarks {
    const p = this.catalog.profile, t = this.catalog.economy.tribute;
    return { levels: p.towerLevels.reduce((a, l) => a + l, p.baseLevel), tributeSince: t.since, tributeCapSeconds: t.capSeconds };
  }

  /**
   * The hub's signals since the last look (MP-21): Daily Orders claimed, upgrades completed and Tribute collected, paid
   * to the live event by its sources. Upgrades and Tribute outside an event (or before events unlock) only move the
   * marks. Returns the embers added.
   */
  syncHub(now: number): number {
    const e = this.live(now), on = e !== null && this.open;
    return this.syncOrders(now) + this.progress.syncHub(on ? e : null, this.hubMarks());
  }

  /**
   * Pays `count` of a signal to the live event once per `key` (MP-20's Daily Road reports "dailyRoad" with its run's
   * key). Returns the embers added (0 outside an event, before the unlock, or when the key already counted).
   */
  creditSignal(now: number, signal: EmberSignal, count: number, key: string): number {
    const e = this.live(now);
    return e && this.open ? this.progress.creditSignals(e, key, { [signal]: count }) : 0;
  }

  /** Reached, unclaimed nodes of the live event (the More row's red count). */
  claimable(now: number): number {
    const e = this.live(now);
    return e && this.open ? this.progress.claimable(e).length : 0;
  }

  /** Claims a node of the live event: pays it (a chest is banked with its gear). Null when not claimable. */
  claimNode(now: number, node: number): NodeClaim | null {
    const e = this.live(now);
    if (!e || !this.open) return null;
    const reward = this.progress.claim(e, node);
    if (!reward) return null;
    const paid = payReward(this.catalog.balance, reward, {
      profile: this.catalog.profile, wallet: this.catalog.economy.wallet, level: this.catalog.highestLevelCleared,
      seed: eventNodeSeed(this.catalog.playerId, e, node), at: now, ref: `${e.id}.${node + 1}`,
    });
    // Marks (shops.json event.sources.provingNodes; Valor until 2026-09-30): each node of a rotation week (any kind) pays once, as the claim itself does.
    const nodeValue = this.catalog.balance.shops.event.sources.provingNodes[node];
    const eventMarks = e.recurring && nodeValue !== undefined ? this.catalog.shops.creditEventMarks(nodeValue, now, `${e.id}.${node + 1}`) : 0;
    return { node, paid, gear: paid.chest ? this.catalog.bankChest(paid.chest) : [], eventMarks };
  }

  /** This week's Trial (locked at the first look), or null while it is locked for the player's progress. */
  trial(now: number): WeeklyTrial | null { return this.trials.current(this.data, now, this.catalog.highestLevelCleared); }
  trialRun(t: WeeklyTrial): LevelModifiers { return trialModifiers(t.modifier); }

  /** A cleared level with all three stars has its Heroic tier. */
  heroicOpen(level: number): boolean { return this.catalog.isCleared(level) && heroicOpen(this.catalog.starsFor(level)); }
  heroicRun(): LevelModifiers { return heroicModifiers(this.data); }
  heroicMarks(level: number): number { return heroicMarks(this.data, level); }
  /** MP-23: Trophies a level's first Heroic win pays. */
  get heroicTrophies(): number { return this.data.heroic.trophies; }
  /** Levels whose Heroic tier is open but not yet won. */
  heroicWaiting(): number[] {
    return Array.from({ length: this.catalog.highestLevelCleared }, (_, i) => i + 1).filter((l) => this.heroicOpen(l) && !this.trials.hasHeroic(l));
  }

  /** Heroic, the locked Trial, or a plain run. */
  runKind(level: number, modifiers: LevelModifiers): RunKind {
    // MP-25: a farm twist borrows a Trial modifier but is never the Trial (nor Heroic): a replay with more gold
    if (modifiers.farm !== undefined) return null;
    if (sameModifiers(modifiers, heroicModifiers(this.data))) return "heroic";
    const t = this.trials.locked(this.data);
    return t && t.level === level && sameModifiers(modifiers, trialModifiers(t.modifier)) ? "trial" : null;
  }

  /**
   * A run ended: embers for the live event (once per run), and on a win the Heroic star with its Marks (first time on
   * the level) or the week's Trial reward (first win of the week). Safe to call again for the same run: nothing pays twice.
   */
  finishRun(r: FinishedRun): RunOutcome {
    const kind = this.runKind(r.level, r.modifiers);
    const e = this.live(r.now);
    const signals = battleSignals(this.catalog.balance, e, {
      level: r.level, won: r.won, stagesCleared: r.stagesCleared, kind, stars: r.stars ?? 0, leaks: r.leaks ?? 0, counts: r.counts ?? {}, rareKills: r.rareKills ?? 0,
    });
    // Daily Road (MP-20) pays its embers once a day, with its reward (app dailyRoad.ts): its runs earn none here; a
    // challenge code's run (MP-28) earns none at all.
    const embers = e && this.open && r.modifiers.daily === undefined && r.modifiers.challenge === undefined ? this.progress.creditSignals(e, r.key, signals) : 0;
    let heroicFirst = false, trialReward: PaidReward | null = null, gear: GearItem[] = [], eventMarks = 0;
    const marks = this.heroicMarks(r.level), sources = this.catalog.balance.shops.event.sources;
    if (r.won && kind === "heroic" && this.trials.recordHeroicWin(r.level)) {
      heroicFirst = true;
      this.catalog.economy.wallet.grant("marks", marks, "event", r.now, `heroic.${r.level}`);
      this.catalog.economy.wallet.grant("trophies", this.heroicTrophies, "event", r.now, `heroic.${r.level}`);
      eventMarks += this.catalog.shops.creditEventMarks(sources.heroicFirstWin, r.now, `heroic.${r.level}`);
    }
    if (r.won && kind === "trial" && this.trials.recordTrialWin()) {
      const week = this.trials.week;
      trialReward = payReward(this.catalog.balance, this.data.trial.reward, {
        profile: this.catalog.profile, wallet: this.catalog.economy.wallet, level: this.catalog.highestLevelCleared,
        seed: metaSeed(this.catalog.playerId, TRIAL_CHEST_TAG, week), at: r.now, ref: `trial.${week}`,
      });
      if (trialReward.chest) gear = this.catalog.bankChest(trialReward.chest);
      eventMarks += this.catalog.shops.creditEventMarks(sources.trialWin, r.now, `trial.${week}`);
    }
    return { kind, event: e, embers, heroicFirst, heroicMarks: heroicFirst ? marks : 0, heroicTrophies: heroicFirst ? this.heroicTrophies : 0, trialReward, gear, eventMarks };
  }
}
